import React, { createContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { analyticsService } from '@/services/AnalyticsService';
import { toast } from '@/stores/notificationStore';

/**
 * Synchronously retrieves and parses the cached Supabase session from localStorage.
 * Enables Turn 0 optimistic rendering without auth flicker or guest flashes on page reload.
 */
function getCachedSupabaseSession(): Session | null {
    if (typeof window === 'undefined') return null;
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
                const item = localStorage.getItem(key);
                if (item) {
                    const parsed = JSON.parse(item);
                    if (parsed && (parsed.access_token || parsed.user)) {
                        return parsed as Session;
                    }
                }
            }
        }
    } catch (e) {
        console.warn('[Auth] Failed to parse cached session from localStorage:', e);
    }
    return null;
}

interface AuthContextType {
    user: User | null;
    session: Session | null;
    loading: boolean;
    isAdmin: boolean;
    isPremium: boolean;
    credits?: number;
    signInWithGoogle: () => Promise<void>;
    signInWithFacebook: () => Promise<void>;
    signInWithPassword: (email: string, pass: string) => Promise<void>;
    signOut: () => Promise<void>;
    isLoginModalOpen: boolean;
    setIsLoginModalOpen: (open: boolean) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const initialSession = useMemo(() => getCachedSupabaseSession(), []);
    const [user, setUser] = useState<User | null>(() => initialSession?.user ?? null);
    const [session, setSession] = useState<Session | null>(() => initialSession);
    const [loading, setLoading] = useState<boolean>(() => !initialSession);
    const [credits, setCredits] = useState<number>(() => initialSession?.user?.app_metadata?.ai_credits ?? 0);

    // Decoupled login modal state to prevent searchParams-induced re-renders on route navigation
    const [isLoginModalOpen, setLocalIsLoginModalOpen] = useState<boolean>(() => {
        if (typeof window === 'undefined') return false;
        return new URLSearchParams(window.location.search).get('auth') === 'login';
    });

    const navigate = useNavigate();

    const setIsLoginModalOpen = useCallback((open: boolean) => {
        setLocalIsLoginModalOpen(open);
        try {
            const url = new URL(window.location.href);
            if (open) {
                url.searchParams.set('auth', 'login');
            } else {
                url.searchParams.delete('auth');
            }
            window.history.replaceState(null, '', url.pathname + url.search + url.hash);
        } catch {
            // Ignore if in non-standard environment
        }
    }, []);

    const authPopupRef = useRef<Window | null>(null);
    const loggedInUserRef = useRef<string | null>(null);
    const initialLoadCompleteRef = useRef(false);

    useEffect(() => {
        // Check active sessions and sets the user
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
            const activeUser = session?.user ?? null;
            setUser(activeUser);
            setCredits(activeUser?.app_metadata?.ai_credits ?? 0);
            if (activeUser) {
                loggedInUserRef.current = activeUser.id;
            }
            initialLoadCompleteRef.current = true;
            setLoading(false);
        }).catch((err) => {
            console.error('[Auth] getSession failed:', err);
            initialLoadCompleteRef.current = true;
            setLoading(false);
        });

        // Listen for changes on auth state (sign in, sign out, etc.)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            try {
                // Guard against premature SIGNED_OUT event setting loading to false before getSession has resolved
                if (!session && !initialLoadCompleteRef.current) {
                    return;
                }

                setSession(session);
                setUser(session?.user ?? null);
                setCredits(session?.user?.app_metadata?.ai_credits ?? 0);
                setLoading(false);

                if (session?.user) {
                    // Identify the user for future events, but don't trigger a 'login' event automatically
                    analyticsService.identify(session.user.id, { email: session.user.email });
                }

                // Handle special events for accurate analytics
                if (_event === 'INITIAL_SESSION' && session?.user) {
                    // We track an app_reload. If it's been >30 mins, AnalyticsService will automatically prepend a 'login' event.
                    analyticsService.track('app_reload', { method: 'session' });

                    loggedInUserRef.current = session.user.id;
                    // Refresh token in background — ensures app_metadata (ai_credits, roles)
                    // reflects any server-side changes since the last JWT was issued.
                    void supabase.auth.refreshSession();
                }

                // Clean URL hash if session is established and hash contains auth tokens or is just empty hash
                if (session && window.location.hash && (window.location.hash.includes('access_token') || window.location.hash.includes('error_description') || window.location.hash === '#')) {
                    window.history.replaceState(null, '', window.location.href.split('#')[0]);
                }

                // Log analytics on explicit sign in
                if (_event === 'SIGNED_IN') {
                    // Only track if this is actually a transition from logged-out to logged-in
                    // or a different user than before (to avoid noise on token refresh)
                    if (loggedInUserRef.current !== session?.user?.id) {
                        // Detect if this is a fresh sign-up (within the last 15 seconds)
                        const createdAt = session?.user?.created_at ? new Date(session.user.created_at).getTime() : 0;
                        const now = Date.now();
                        const isNewUser = (now - createdAt) < 15000;

                        if (isNewUser) {
                            analyticsService.track('sign_up', {
                                method: session?.user?.app_metadata?.provider || 'email'
                            });
                        }

                        analyticsService.track('login', {
                            method: session?.user?.app_metadata?.provider || 'email'
                        });
                        
                        loggedInUserRef.current = session?.user?.id || null;
                    }

                    // Clear popup ref on sign-in (poll interval will also catch this)
                    try {
                        if (authPopupRef.current && !authPopupRef.current.closed) {
                            authPopupRef.current.close();
                        }
                    } catch (e) {
                        console.warn('[Auth] Could not close OAuth popup directly due to cross-origin policies:', e);
                    }
                    authPopupRef.current = null;

                    // If logging in from the landing page, redirect to the app to start calculating!
                    if (window.location.pathname === '/' || window.location.pathname === '/home') {
                        navigate('/calculator');
                    }
                } else if (_event === 'SIGNED_OUT') {
                    if (loggedInUserRef.current !== null) {
                        analyticsService.track('logout');
                        analyticsService.identify(null); // Clear the identity context
                        loggedInUserRef.current = null;
                    }
                }
            } catch (authCallbackErr) {
                console.error('[Auth] onAuthStateChange callback error:', authCallbackErr);
                // Still try to update core state even if analytics/side-effects failed
                setSession(session);
                setUser(session?.user ?? null);
                setLoading(false);
            }
        });

        // Poll for popup closure every 500ms.
        // When the popup closes (auto or manually), force-sync the session.
        // This is the ONLY cross-window mechanism needed — Supabase already syncs
        // session via localStorage storage events, but getSession() ensures React
        // state is immediately consistent.
        let popupPoll: ReturnType<typeof setInterval> | null = null;
        let popupPollStartTime = 0;
        const POPUP_POLL_MAX_MS = 90_000; // 90 seconds safety cap to prevent infinite COOP spam
        const startPolling = () => {
            if (popupPoll) return;
            popupPollStartTime = Date.now();
            popupPoll = setInterval(async () => {
                // Safety timeout: stop polling after 90 seconds to prevent infinite COOP error spam
                if (Date.now() - popupPollStartTime > POPUP_POLL_MAX_MS) {
                    console.warn('[Auth] Popup poll exceeded 90s safety cap — stopping. Session sync will rely on storage events.');
                    clearInterval(popupPoll!);
                    popupPoll = null;
                    authPopupRef.current = null;
                    // One final session sync attempt
                    const { data: { session: s } } = await supabase.auth.getSession();
                    if (s) { setSession(s); setUser(s.user); }
                    return;
                }
                try {
                    if (authPopupRef.current?.closed) {
                        clearInterval(popupPoll!);
                        popupPoll = null;
                        authPopupRef.current = null;
                        // Force session read in case storage event was missed
                        const { data: { session: s } } = await supabase.auth.getSession();
                        if (s) {
                            setSession(s);
                            setUser(s.user);
                        }
                    }
                } catch (_e) {
                    // Ignore DOMExceptions thrown when checking .closed on cross-origin frames
                    // Log only once every ~10 seconds to reduce spam
                    const elapsed = Date.now() - popupPollStartTime;
                    if (elapsed % 10000 < 600) {
                        console.warn(`[Auth] COOP block on popup.closed check (${Math.round(elapsed/1000)}s elapsed)`);
                    }
                }
            }, 500);
        };
        // Expose startPolling so signInWithOAuth can trigger it
        (window as unknown as Record<string, unknown>).__startAuthPopupPoll = startPolling;

        return () => {
            subscription.unsubscribe();
            if (popupPoll) clearInterval(popupPoll);
        };
    }, [navigate]);

    const signInWithOAuth = useCallback(async (provider: 'google' | 'facebook') => {
        // Calculate window position
        const width = 600;
        const height = 800;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        // Open popup synchronously *before* the await to bypass iOS Safari blockers
        const newWindow = window.open(
            '',
            `Connect with ${provider}`,
            `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,status=yes`
        );
        authPopupRef.current = newWindow;

        // Close the login modal immediately
        setIsLoginModalOpen(false);

        const { data, error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
                redirectTo: `${window.location.origin}/auth-callback.html`,
                skipBrowserRedirect: true, // Don't redirect current window
            },
        });

        if (error) {
            if (authPopupRef.current) {
                authPopupRef.current.close();
            }
            throw error;
        }

        if (data?.url) {
            // Check if the popup was blocked or closed immediately by Safari
            if (!authPopupRef.current || authPopupRef.current.closed || typeof authPopupRef.current.closed === 'undefined') {
                // The popup failed to open. Provide a graceful fallback Toast.
                toast.error(`Pop-ups are blocked. Please allow pop-ups or [click here](${data.url}) to continue.`, Infinity); // Using Infinity for a persistent toast
                return;
            }

            // The popup exists, inject the auth URL into it
            authPopupRef.current.location.href = data.url;

            // Start polling for popup closure (defined in useEffect)
            const startPoll = (window as unknown as Record<string, unknown>).__startAuthPopupPoll;
            if (typeof startPoll === 'function') startPoll();
        }
    }, [setIsLoginModalOpen]);

    const signInWithGoogle = useCallback(async () => {
        await signInWithOAuth('google');
    }, [signInWithOAuth]);

    const signInWithFacebook = useCallback(async () => {
        await signInWithOAuth('facebook');
    }, [signInWithOAuth]);

    const signOut = useCallback(async () => {
        const { error } = await supabase.auth.signOut({ scope: 'local' });
        if (error) throw error;
    }, []);

    const signInWithPassword = useCallback(async (email: string, pass: string) => {
        const { error } = await supabase.auth.signInWithPassword({
            email,
            password: pass,
        });
        if (error) throw error;
    }, []);

    const isAdmin = useMemo(() => {
        return user?.app_metadata?.role === 'admin';
    }, [user]);

    const isPremium = useMemo(() => {
        const role = user?.app_metadata?.role;
        return role === 'premium' || role === 'admin';
    }, [user]);

    // const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    // Track user activity to keep the session alive
    useEffect(() => {
        const updateActivity = () => localStorage.setItem('spirit_last_active', Date.now().toString());
        // Throttle might be needed in a huge app, but passive listeners are cheap enough
        window.addEventListener('click', updateActivity, { passive: true });
        window.addEventListener('scroll', updateActivity, { passive: true });
        window.addEventListener('keypress', updateActivity, { passive: true });
        return () => {
            window.removeEventListener('click', updateActivity);
            window.removeEventListener('scroll', updateActivity);
            window.removeEventListener('keypress', updateActivity);
        };
    }, []);

    // Realtime Credit Ledger Auto-Refresh Listener
    useEffect(() => {
        if (!user) return;

        const channel = supabase
            .channel(`user_credits_${user.id}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'credit_ledger',
                    filter: `user_id=eq.${user.id}`,
                },
                (payload) => {
                    const newAmount = payload.new?.amount;
                    if (typeof newAmount === 'number') {
                        setCredits(prev => prev + newAmount);
                    }
                    if (import.meta.env.DEV) {
                        // eslint-disable-next-line no-console
                        console.log('[Auth] Credit ledger insert detected. Updated local credits balance state by:', newAmount);
                    }
                    // Trigger background refresh to keep cached JWT app_metadata in sync
                    void supabase.auth.refreshSession();
                }
            )
            .subscribe();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [user]);

    // Memoize the context value to prevent unnecessary re-renders
    const value = useMemo(() => ({
        user,
        session,
        loading,
        isAdmin,
        isPremium,
        credits,
        signInWithGoogle,
        signInWithFacebook,
        signInWithPassword,
        signOut,
        isLoginModalOpen,
        setIsLoginModalOpen,
    }), [user, session, loading, isAdmin, isPremium, credits, signInWithGoogle, signInWithFacebook, signInWithPassword, signOut, isLoginModalOpen, setIsLoginModalOpen]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
